import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { poApi } from '../../utils/purchasingApi';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import './Purchasing.css';

const PO_STATUSES = ['DRAFT','PENDING_APPROVAL','MANAGER_APPROVED','CEO_APPROVED','RECEIVED','CANCELLED'];

const STATUS_LABEL = {
  DRAFT:            'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  MANAGER_APPROVED: 'Manager Approved',
  CEO_APPROVED:     'CEO Approved',
  RECEIVED:         'Received',
  CANCELLED:        'Cancelled',
};

const STATUS_CLASS = {
  DRAFT:            'purch-status--draft',
  PENDING_APPROVAL: 'purch-status--pending',
  MANAGER_APPROVED: 'purch-status--manager-approved',
  CEO_APPROVED:     'purch-status--ceo-approved',
  RECEIVED:         'purch-status--received',
  CANCELLED:        'purch-status--cancelled',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-EG') : '—';
const fmtNum  = (n) => n != null ? Number(n).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

// ── PO Detail Panel ───────────────────────────────────────

function PoDetail({ po: initial, onClose, onRefresh, canCreate, canApprove, canReceive }) {
  const [po, setPo]         = useState(initial);
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(null);
  const [error, setError]     = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel]     = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    poApi.getById(initial.id).then(setPo).catch(() => {}).finally(() => setLoading(false));
  }, [initial.id]);

  const act = async (label, fn) => {
    setActing(label); setError(null);
    try { const res = await fn(); setPo(res); onRefresh(); }
    catch (e) { setError(e.message); }
    finally { setActing(null); }
  };

  const handleSubmit        = () => act('submit',  () => poApi.submit(po.id));
  const handleApproveManager= () => act('manager', () => poApi.approveManager(po.id));
  const handleApproveCeo    = () => act('ceo',     () => poApi.approveCeo(po.id));
  const handleReceive       = () => act('receive', () => poApi.receive(po.id));
  const handleCancel        = () => {
    if (!cancelReason.trim()) return;
    act('cancel', () => poApi.cancel(po.id, cancelReason));
    setShowCancel(false);
  };

  const canBeCancelled = !['RECEIVED','CANCELLED'].includes(po.status);

  return (
    <div className="purch-detail-overlay" onClick={onClose}>
      <div className="purch-detail-panel" onClick={(e) => e.stopPropagation()}>
        <div className="purch-detail-header">
          <div>
            <h2 className="purch-detail-title">PO-{po.poNumber}</h2>
            <span className={`purch-status ${STATUS_CLASS[po.status]}`}>{STATUS_LABEL[po.status]}</span>
          </div>
          <button className="purch-btn purch-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>

        {loading ? <LoadingSpinner variant="default" /> : (
          <div className="purch-detail-body">
            {error && <div className="purch-error-banner" style={{ marginBottom: 12 }}><i className="fas fa-exclamation-circle" /> {error}</div>}

            <div className="purch-detail-meta">
              <div><span className="purch-meta-key">Date</span> {fmtDate(po.date)}</div>
              <div><span className="purch-meta-key">Vendor</span> {po.vendor?.name}</div>
              <div><span className="purch-meta-key">Currency</span> {po.currency?.code}</div>
              <div><span className="purch-meta-key">Total</span> {fmtNum(po.totalAmount)}</div>
              {po.expectedDelivery && <div><span className="purch-meta-key">Expected</span> {fmtDate(po.expectedDelivery)}</div>}
              {po.entity   && <div><span className="purch-meta-key">Entity</span> {po.entity.code}</div>}
              {po.pr       && <div><span className="purch-meta-key">Linked PR</span> PR-{po.pr.prNumber}</div>}
              <div><span className="purch-meta-key">Created by</span> {po.createdBy?.fullName}</div>
              {po.submittedBy       && <div><span className="purch-meta-key">Submitted by</span> {po.submittedBy.fullName}</div>}
              {po.managerApprovedBy && <div><span className="purch-meta-key">Mgr Approved</span> {po.managerApprovedBy.fullName}</div>}
              {po.ceoApprovedBy     && <div><span className="purch-meta-key">CEO Approved</span> {po.ceoApprovedBy.fullName}</div>}
              {po.receivedBy        && <div><span className="purch-meta-key">Received by</span> {po.receivedBy.fullName}</div>}
              {po.cancelledBy       && <div><span className="purch-meta-key">Cancelled by</span> {po.cancelledBy.fullName}</div>}
              {po.cancellationReason && <div className="purch-meta-full"><span className="purch-meta-key">Cancel reason</span> {po.cancellationReason}</div>}
              {po.notes && <div className="purch-meta-full"><span className="purch-meta-key">Notes</span> {po.notes}</div>}
            </div>

            <table className="purch-lines-table">
              <thead>
                <tr><th>#</th><th>Description</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th><th>Account</th></tr>
              </thead>
              <tbody>
                {po.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.lineNumber}</td>
                    <td>{l.description}</td>
                    <td>{Number(l.quantity).toLocaleString()}</td>
                    <td>{l.unit || '—'}</td>
                    <td className="purch-num">{fmtNum(l.unitPrice)}</td>
                    <td className="purch-num">{fmtNum(l.lineTotal)}</td>
                    <td style={{ fontSize: '0.75rem', color: '#64748b' }}>{l.account ? `${l.account.code}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="purch-total-label">Total</td>
                  <td className="purch-num purch-total-val">{fmtNum(po.totalAmount)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>

            {/* Workflow actions */}
            <div className="purch-action-bar">
              {po.status === 'DRAFT' && canCreate && (
                <>
                  <button className="purch-btn purch-btn--ghost"
                    onClick={() => navigate(`/purchasing/orders/${po.id}/edit`)}>
                    <i className="fas fa-edit" /> Edit
                  </button>
                  <button className="purch-btn purch-btn--primary" onClick={handleSubmit} disabled={!!acting}>
                    {acting === 'submit' ? <LoadingSpinner variant="button" /> : 'Submit for Approval'}
                  </button>
                </>
              )}
              {po.status === 'PENDING_APPROVAL' && canApprove && (
                <button className="purch-btn purch-btn--success" onClick={handleApproveManager} disabled={!!acting}>
                  {acting === 'manager' ? <LoadingSpinner variant="button" /> : <><i className="fas fa-user-check" /> Manager Approve</>}
                </button>
              )}
              {po.status === 'MANAGER_APPROVED' && canApprove && (
                <button className="purch-btn purch-btn--success" onClick={handleApproveCeo} disabled={!!acting}>
                  {acting === 'ceo' ? <LoadingSpinner variant="button" /> : <><i className="fas fa-check-double" /> CEO Approve</>}
                </button>
              )}
              {po.status === 'CEO_APPROVED' && canReceive && (
                <button className="purch-btn purch-btn--primary" onClick={handleReceive} disabled={!!acting}>
                  {acting === 'receive' ? <LoadingSpinner variant="button" /> : <><i className="fas fa-box-open" /> Mark Received</>}
                </button>
              )}
              {canBeCancelled && canCreate && (
                <button className="purch-btn purch-btn--danger-outline" onClick={() => setShowCancel(true)} disabled={!!acting}>
                  <i className="fas fa-ban" /> Cancel PO
                </button>
              )}
            </div>

            {showCancel && (
              <div className="purch-cancel-box">
                <label className="purch-label">Cancellation reason *</label>
                <textarea className="purch-input purch-textarea" rows={2}
                  value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                <div className="purch-cancel-actions">
                  <button className="purch-btn purch-btn--ghost purch-btn--sm" onClick={() => setShowCancel(false)}>Back</button>
                  <button className="purch-btn purch-btn--danger purch-btn--sm" onClick={handleCancel} disabled={!!acting}>
                    {acting === 'cancel' ? <LoadingSpinner variant="button" /> : 'Confirm Cancel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────

export default function PurchaseOrdersPage() {
  const { hasPermission } = useAuth();
  const canCreate  = hasPermission('purchases.po.create');
  const canApprove = hasPermission('purchases.po.approve');
  const canReceive = hasPermission('purchases.po.receive');
  const navigate   = useNavigate();
  const location   = useLocation();

  const [data, setData]       = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const LIMIT = 50;

  const [selected, setSelected] = useState(null);
  const [toast, setToast]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await poApi.list({ status: statusFilter || undefined, search: search || undefined, page, limit: LIMIT });
      setData(res);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [statusFilter, search, page]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  // Show toast passed via navigation state (from PO form)
  useEffect(() => {
    if (location.state?.toast) {
      showToast(location.state.toast);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(data.total / LIMIT);

  return (
    <div className="purch-page">
      {toast && (
        <div className={`purch-toast purch-toast--${toast.type}`}>
          <i className={`fas ${toast.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`} /> {toast.msg}
        </div>
      )}

      <div className="purch-page-header">
        <div className="purch-title-group">
          <h1 className="purch-page-title"><i className="fas fa-file-invoice" style={{ marginRight: 10 }} />Purchase Orders</h1>
          <span className="purch-badge">{data.total} total</span>
        </div>
        {canCreate && (
          <button className="purch-btn purch-btn--primary" onClick={() => navigate('/purchasing/orders/new')}>
            <i className="fas fa-plus" style={{ marginRight: 6 }} /> New PO
          </button>
        )}
      </div>

      <div className="purch-filters">
        <div className="purch-search-wrap">
          <i className="fas fa-search purch-search-icon" />
          <input className="purch-search" placeholder="Search by vendor or notes…"
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="purch-select purch-select--sm" value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {PO_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      {loading ? <LoadingSpinner variant="fullpage" />
        : error ? <div className="purch-error-banner"><i className="fas fa-exclamation-circle" /> {error}</div>
        : data.items.length === 0 ? (
          <div className="purch-empty">
            <i className="fas fa-file-invoice" />
            <p>{search || statusFilter ? 'No purchase orders match your filters.' : 'No purchase orders yet.'}</p>
          </div>
        ) : (
          <>
            <div className="purch-table-wrap">
              <table className="purch-table">
                <thead>
                  <tr><th>PO #</th><th>Date</th><th>Vendor</th><th>Entity</th><th>Currency</th><th style={{ textAlign:'right' }}>Total</th><th>Status</th><th>Expected</th></tr>
                </thead>
                <tbody>
                  {data.items.map((po) => (
                    <tr key={po.id} className="purch-row" onClick={() => setSelected(po)}>
                      <td className="purch-number">PO-{po.poNumber}</td>
                      <td>{fmtDate(po.date)}</td>
                      <td>{po.vendor?.name}</td>
                      <td>{po.entity?.code ?? '—'}</td>
                      <td>{po.currency?.code}</td>
                      <td className="purch-num">{fmtNum(po.totalAmount)}</td>
                      <td><span className={`purch-status ${STATUS_CLASS[po.status]}`}>{STATUS_LABEL[po.status]}</span></td>
                      <td>{fmtDate(po.expectedDelivery)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="purch-pagination">
                <button className="purch-btn purch-btn--ghost purch-btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><i className="fas fa-chevron-left" /></button>
                <span className="purch-page-info">Page {page} of {totalPages}</span>
                <button className="purch-btn purch-btn--ghost purch-btn--sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><i className="fas fa-chevron-right" /></button>
              </div>
            )}
          </>
        )}

      {selected && (
        <PoDetail
          po={selected}
          onClose={() => setSelected(null)}
          onRefresh={() => { load(); showToast('Purchase order updated.'); }}
          canCreate={canCreate}
          canApprove={canApprove}
          canReceive={canReceive}
        />
      )}
    </div>
  );
}
